import { V2_ENDPOINT } from "@/consts";
import {
  trackWalletCreated,
  trackWalletCreateError,
  trackWalletDeleted,
  trackWalletDeleteError,
  trackWalletSendCompleted,
  trackWalletSendError,
} from "@/lib/analytics";
import { authenticatedFetch, authenticatedFetchJson } from "@/lib/authenticatedFetch";
import { sortOrganizationWallets } from "@/lib/wallets";
import { showToast } from "@/lib/toast";
import type {
  CreateWalletRequest,
  CreateWalletResponse,
  ListWalletsResponse,
  OrganizationWallet,
  WalletAccountConfig,
  WithdrawWalletRequest,
} from "@/types/wallet";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Module-scoped empty array so the hook returns a stable reference when
// `data` is undefined (before first fetch resolves or when disabled).
// Returning `walletsData || []` would create a new `[]` each render and
// cause consumer effects / context values that depend on `wallets` to
// fire repeatedly.
const EMPTY_WALLETS: OrganizationWallet[] = [];

// First-page cap for the wallets list. Orgs with >100 wallets see only
// the first 100 here — the canvas-level `OrganizationWalletsProvider` and
// AI-streaming checks fan out across dozens of canvas nodes, so paginating
// further would multiply outbound requests without a clear UX win. If a
// consumer needs the full set (e.g. a dedicated wallets admin page), query
// the API directly rather than going through this store.
const WALLETS_PAGE_LIMIT = 100;

/**
 * Hook for managing organization wallets
 *
 * This hook provides functionality to:
 * - List all wallets for an organization
 * - Create new wallets within the organization's Turnkey sub-org
 * - Auto-refetch on wallet creation
 *
 * @param organizationId - The organization ID to manage wallets for
 * @returns Wallet management utilities and state
 */
export function useOrganizationWallets(organizationId?: string) {
  const queryClient = useQueryClient();

  // List wallets query
  const {
    data: walletsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["organization-wallets", organizationId],
    queryFn: async (): Promise<OrganizationWallet[]> => {
      if (!organizationId) {
        return [];
      }

      if (!V2_ENDPOINT) {
        throw new Error("V2_ENDPOINT not configured");
      }

      const response = await authenticatedFetchJson<ListWalletsResponse>(
        `${V2_ENDPOINT}/v1/organizations/${organizationId}/wallets?limit=${WALLETS_PAGE_LIMIT}&offset=0`,
        {
          orgId: organizationId,
        },
      );

      if (response.code !== 0) {
        throw new Error(`API error: ${response.message}`);
      }

      return sortOrganizationWallets(response.data.items || []);
    },
    enabled: !!organizationId,
    staleTime: 30000, // 30 seconds
  });

  // Create wallet mutation
  const createWalletMutation = useMutation({
    mutationFn: async ({
      walletName,
      accounts,
    }: {
      walletName: string;
      accounts?: WalletAccountConfig[];
    }): Promise<OrganizationWallet> => {
      if (!organizationId) {
        throw new Error("Organization ID is required");
      }

      if (!V2_ENDPOINT) {
        throw new Error("V2_ENDPOINT not configured");
      }

      const requestBody: CreateWalletRequest = { name: walletName };
      if (accounts && accounts.length > 0) {
        requestBody.accounts = accounts;
      }

      const response = await authenticatedFetch<CreateWalletResponse>(
        `${V2_ENDPOINT}/v1/organizations/${organizationId}/wallets`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
          orgId: organizationId,
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || "Failed to create wallet");
      }

      const data = await response.json();

      if (data.code !== 0) {
        throw new Error(data.message || "Failed to create wallet");
      }

      return data.data;
    },
    onSuccess: wallet => {
      queryClient.invalidateQueries({ queryKey: ["organization-wallets", organizationId] });
      showToast.success(`Wallet "${wallet.name}" created successfully`);
      trackWalletCreated({ walletId: wallet.id });
    },
    onError: (error: Error) => {
      showToast.error(error.message || "Failed to create wallet");
      trackWalletCreateError({ errorMessage: error.message || "Failed to create wallet" });
    },
  });

  // Set default wallet mutation
  const setDefaultWalletMutation = useMutation({
    mutationFn: async (walletId: string): Promise<void> => {
      if (!organizationId) {
        throw new Error("Organization ID is required");
      }

      if (!V2_ENDPOINT) {
        throw new Error("V2_ENDPOINT not configured");
      }

      const response = await authenticatedFetch(
        `${V2_ENDPOINT}/v1/organizations/${organizationId}/wallets/${walletId}/default`,
        {
          method: "PUT",
          orgId: organizationId,
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || "Failed to set default wallet");
      }
    },
    onSuccess: () => {
      // Invalidate and refetch wallets
      queryClient.invalidateQueries({ queryKey: ["organization-wallets", organizationId] });
      showToast.success("Default wallet updated");
    },
    onError: (error: Error) => {
      showToast.error(error.message || "Failed to set default wallet");
    },
  });

  // Rename wallet mutation
  const renameWalletMutation = useMutation({
    mutationFn: async ({ walletId, name }: { walletId: string; name: string }): Promise<void> => {
      if (!organizationId) {
        throw new Error("Organization ID is required");
      }

      if (!V2_ENDPOINT) {
        throw new Error("V2_ENDPOINT not configured");
      }

      const response = await authenticatedFetch(
        `${V2_ENDPOINT}/v1/organizations/${organizationId}/wallets/${walletId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ name }),
          orgId: organizationId,
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || "Failed to rename wallet");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization-wallets", organizationId] });
      showToast.success("Wallet renamed");
    },
    onError: (error: Error) => {
      showToast.error(error.message || "Failed to rename wallet");
    },
  });

  // Delete wallet mutation
  const deleteWalletMutation = useMutation({
    mutationFn: async (walletId: string): Promise<void> => {
      if (!organizationId) {
        throw new Error("Organization ID is required");
      }

      if (!V2_ENDPOINT) {
        throw new Error("V2_ENDPOINT not configured");
      }

      const response = await authenticatedFetch(
        `${V2_ENDPOINT}/v1/organizations/${organizationId}/wallets/${walletId}`,
        {
          method: "DELETE",
          orgId: organizationId,
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || "Failed to delete wallet");
      }
    },
    onSuccess: (_result, walletId) => {
      queryClient.invalidateQueries({ queryKey: ["organization-wallets", organizationId] });
      showToast.success("Wallet deleted successfully");
      trackWalletDeleted(walletId);
    },
    onError: (error: Error, walletId) => {
      showToast.error(error.message || "Failed to delete wallet");
      trackWalletDeleteError({ walletId, errorMessage: error.message || "Failed to delete wallet" });
    },
  });

  // Withdraw mutation
  const withdrawMutation = useMutation({
    mutationFn: async ({
      walletId,
      request,
    }: {
      walletId: string;
      request: WithdrawWalletRequest;
    }): Promise<{ transactionHash: string; blockNumber?: number }> => {
      if (!organizationId) {
        throw new Error("Organization ID is required");
      }

      if (!V2_ENDPOINT) {
        throw new Error("V2_ENDPOINT not configured");
      }

      const response = await authenticatedFetch(
        `${V2_ENDPOINT}/v1/organizations/${organizationId}/wallets/${walletId}/withdraw`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(request),
          orgId: organizationId,
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || "Failed to withdraw");
      }

      const data = await response.json();

      if (data.code !== 0) {
        throw new Error(data.message || "Failed to withdraw");
      }

      return data.data;
    },
    onSuccess: (result, { walletId, request }) => {
      trackWalletSendCompleted({
        walletId,
        chainId: request.chainId,
        tokenAddress: request.tokenAddress,
        transactionHash: result.transactionHash,
      });
    },
    onError: (error: Error, { walletId, request }) => {
      trackWalletSendError({
        walletId,
        chainId: request.chainId,
        tokenAddress: request.tokenAddress,
        errorMessage: error.message || "Failed to withdraw",
      });
    },
  });

  const wallets = walletsData ?? EMPTY_WALLETS;
  const defaultWallet = wallets.find(w => w.isDefault);

  const createWallet = (walletName: string, accounts?: WalletAccountConfig[]) =>
    createWalletMutation.mutateAsync({ walletName, accounts });

  return {
    wallets,
    defaultWallet,
    isLoading,
    error,
    refetch,
    createWallet,
    isCreatingWallet: createWalletMutation.isPending,
    setDefaultWallet: setDefaultWalletMutation.mutateAsync,
    isSettingDefault: setDefaultWalletMutation.isPending,
    deleteWallet: deleteWalletMutation.mutateAsync,
    isDeleting: deleteWalletMutation.isPending,
    withdraw: withdrawMutation.mutateAsync,
    isWithdrawing: withdrawMutation.isPending,
    renameWallet: renameWalletMutation.mutateAsync,
    isRenaming: renameWalletMutation.isPending,
  };
}

/**
 * Hook for getting a specific wallet by ID
 *
 * @param organizationId - The organization ID
 * @param walletId - The wallet ID to find
 * @returns The wallet if found, or null
 */
export function useOrganizationWallet(organizationId?: string, walletId?: string) {
  const { wallets, isLoading } = useOrganizationWallets(organizationId);

  const wallet = wallets.find(w => w.id === walletId) || null;

  return {
    wallet,
    isLoading,
  };
}

/**
 * Hook for getting wallet by address
 *
 * @param organizationId - The organization ID
 * @param address - The wallet address to find
 * @returns The wallet if found, or null
 */
export function useOrganizationWalletByAddress(organizationId?: string, address?: string) {
  const { wallets, isLoading } = useOrganizationWallets(organizationId);

  const wallet = wallets.find(w => w.address.toLowerCase() === address?.toLowerCase()) || null;

  return {
    wallet,
    isLoading,
  };
}
